import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { CompositionControls } from "@/components/CompositionControls";
import { templates, defaultTemplate, getTemplateLetters, type Template } from "@/data/templates";
import { backgrounds, defaultBackground, getRandomBackground, type Background } from "@/data/backgrounds";

const Index = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(defaultTemplate);
  const [letters, setLetters] = useState<{ [key: string]: string }>(getTemplateLetters(defaultTemplate));
  const [text, setText] = useState("");
  const [scale, setScale] = useState(100);
  const [isManualScale, setIsManualScale] = useState(false); // Track if user has manually adjusted scale
  const [aspectRatio, setAspectRatio] = useState("square");
  const [selectedBackground, setSelectedBackground] = useState<Background>(defaultBackground);

  const handleTemplateChange = (template: Template) => {
    setSelectedTemplate(template);
    setLetters(getTemplateLetters(template));
    // Auto-set the paired background for this template
    const pairedBg = backgrounds.find(bg => bg.id === template.defaultBackgroundId);
    if (pairedBg) {
      setSelectedBackground(pairedBg);
    }
  };

  // Load saved template preference on mount
  useEffect(() => {
    const savedTemplateId = localStorage.getItem("preferredTemplate");
    if (savedTemplateId) {
      const savedTemplate = templates.find(t => t.id === savedTemplateId);
      if (savedTemplate) {
        setSelectedTemplate(savedTemplate);
        setLetters(getTemplateLetters(savedTemplate));
      }
    }
  }, []);

  // Apply UX mode when template changes
  useEffect(() => {
    const root = document.documentElement;

    // Remove all UX mode classes
    root.classList.remove("ux-zen", "ux-cyber", "ux-forest", "ux-earth", "ux-ocean", "ux-magazine", "ux-fire");

    // Add current UX mode class (except default)
    if (selectedTemplate.uxMode !== "default") {
      root.classList.add(selectedTemplate.uxMode);
    }

    // Store in localStorage for persistence
    localStorage.setItem("preferredTemplate", selectedTemplate.id);
  }, [selectedTemplate]);

  const handleScaleChange = (newScale: number) => {
    setScale(newScale);
    setIsManualScale(true); // Activate manual mode when user touches slider
  };

  return (
    <div className="min-h-screen py-3.5 px-3.5 sm:py-4 sm:px-4 md:py-8 md:px-12 transition-all duration-300">
      <div className="max-w-7xl mx-auto w-full">
        <Header />

        {/* Main Layout: Single column on all screen sizes */}
        <div className="w-full grid grid-cols-1 gap-4">
          {/* Canvas and controls */}
          <div>
            <CompositionControls
            letters={letters}
            text={text}
            setText={setText}
            scale={scale}
            setScale={handleScaleChange}
            isManualScale={isManualScale}
            aspectRatio={aspectRatio}
            setAspectRatio={setAspectRatio}
            templates={templates}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={handleTemplateChange}
            backgrounds={backgrounds}
            selectedBackground={selectedBackground}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
